import type { Department, DepartmentParentOption } from '../types/department'

export function flattenDepartmentTree(
  nodes: Department[],
  prefix = '',
  depth = 0,
): DepartmentParentOption[] {
  const options: DepartmentParentOption[] = []

  for (const node of nodes) {
    const label = prefix === '' ? node.name : `${prefix} › ${node.name}`
    options.push({ id: node.id, label, depth })

    if (node.children && node.children.length > 0) {
      options.push(...flattenDepartmentTree(node.children, label, depth + 1))
    }
  }

  return options
}

export function collectDepartmentDescendantIds(node: Department): string[] {
  const ids: string[] = []

  for (const child of node.children ?? []) {
    ids.push(child.id, ...collectDepartmentDescendantIds(child))
  }

  return ids
}

export function findDepartmentInTree(nodes: Department[], id: string): Department | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node
    }

    const match = findDepartmentInTree(node.children ?? [], id)

    if (match) {
      return match
    }
  }

  return null
}

export function collectDepartmentNames(nodes: Department[]): string[] {
  return nodes.flatMap((node) => [node.name, ...collectDepartmentNames(node.children ?? [])])
}

export function insertDepartmentInTree(nodes: Department[], department: Department): Department[] {
  const parentId = department.parent_id
  if (!parentId) {
    return [...nodes, department]
  }

  let inserted = false

  const walk = (items: Department[]): Department[] =>
    items.map((node) => {
      if (node.id === parentId) {
        inserted = true
        const children = [...(node.children ?? []), department]

        return { ...node, has_children: true, children }
      }

      return { ...node, children: walk(node.children ?? []) }
    })

  const next = walk(nodes)

  return inserted ? next : [...nodes, { ...department, parent_id: null }]
}

export function removeDepartmentFromTree(nodes: Department[], id: string): Department[] {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => {
      const children = removeDepartmentFromTree(node.children ?? [], id)

      return {
        ...node,
        children,
        has_children: children.length > 0,
      }
    })
}

export function countDepartmentNodes(nodes: Department[]): number {
  return nodes.reduce((total, node) => {
    return total + 1 + countDepartmentNodes(node.children ?? [])
  }, 0)
}

export function getDepartmentPathLabel(nodes: Department[], id: string): string | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node.name
    }

    const childLabel = getDepartmentPathLabel(node.children ?? [], id)

    if (childLabel) {
      return `${node.name} › ${childLabel}`
    }
  }

  return null
}

export function filterSelectableDepartmentTree(
  nodes: Department[],
  excludedIds: Set<string>,
): Department[] {
  return nodes
    .filter((node) => !excludedIds.has(node.id))
    .map((node) => ({
      ...node,
      children: filterSelectableDepartmentTree(node.children ?? [], excludedIds),
    }))
}
